import React from 'react';
import { ItemStack } from 'components/ItemStack';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Typography,
} from '@mui/material';

import { IMediumArticle } from './Blog.types';

interface BlogCardProps {
  article: IMediumArticle;
}

const descriptionParser = (description: string) => {
  const firstP = description?.split('<p>')[1];
  const sanitizedP = firstP?.replace(/<\/?[^>]+(>|$)/g, '');
  const shortP =
    sanitizedP?.length > 300 ? `${sanitizedP?.slice(0, 300)}...` : sanitizedP;
  return shortP;
};

export const BlogCard: React.FunctionComponent<BlogCardProps> = ({
  article,
}) => {
  const { title, description, link, thumbnail, categories } = article;
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  return (
    <Card sx={styles.card}>
      <Box>
        <CardMedia
          component='img'
          height={width < 490 ? '200' : '300'}
          image={thumbnail}
          alt={title}
        />
        <CardContent sx={styles.cardContent}>
          <Typography gutterBottom variant='h6' component='div'>
            {title}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            align='justify'
            sx={{ mb: 2 }}
          >
            {descriptionParser(description)}
          </Typography>
          <ItemStack items={categories} style={styles.itemStack} />
        </CardContent>
        <CardActions sx={styles.cardActions}>
          <Button size='small'>
            <a href={link} target='_blank' rel='noopener noreferrer'>
              {t('blog.readMore')}
            </a>
          </Button>
        </CardActions>
      </Box>
    </Card>
  );
};

const styles = {
  card: {
    maxWidth: 600,
    mx: 'auto',
    my: { xs: 1, sm: 2, md: 3 },
  },
  cardContent: { pb: 0 },
  cardActions: { pt: 0 },
  itemStack: { fontSize: 11, padding: 3 },
};

export default BlogCard;
